import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import NuevoTurnoModal from "@/components/NuevoTurnoModal";
import {
  Users, Calendar, ClipboardCheck, DollarSign, Plus, Clock, ChevronRight,
} from "lucide-react";

const API = "/api";

const EstadoBadge = ({ estado }) => {
  const cls = {
    pendiente: "badge-pendiente",
    confirmado: "badge-confirmado",
    completado: "badge-completado",
    cancelado: "badge-cancelado",
  };
  return <span className={cls[estado] || "badge-pendiente"}>{estado || "pendiente"}</span>;
};

export default function Dashboard() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTurnos, setRecentTurnos] = useState([]);
  const [todayTurnos, setTodayTurnos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [statsRes, turnosRes] = await Promise.all([
        axios.get(`${API}/turnos/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/turnos?limit=50`, { headers: getAuthHeader() }),
      ]);
      setStats(statsRes.data);
      const all = turnosRes.data;
      setRecentTurnos(all.slice(0, 8));

      const today = new Date().toISOString().split("T")[0];
      const todayFmt = new Date().toLocaleDateString("es-AR").replace(/\//g, "/");
      setTodayTurnos(all.filter((t) => t.fecha_turno === today || t.fecha_turno === todayFmt));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const statCards = stats
    ? [
        { label: "Total turnos", value: stats.total, icon: ClipboardCheck, color: "bg-blue-50", iconColor: "text-blue-600" },
        { label: "Turnos hoy", value: stats.hoy, icon: Calendar, color: "bg-emerald-50", iconColor: "text-emerald-600" },
        { label: "Pacientes únicos", value: stats.pacientes_unicos, icon: Users, color: "bg-violet-50", iconColor: "text-violet-600" },
        { label: "Monto cobrado", value: `$${stats.monto_total?.toLocaleString("es-AR")}`, icon: DollarSign, color: "bg-amber-50", iconColor: "text-amber-600" },
      ]
    : [];

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {new Date().toLocaleDateString("es-AR", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)} data-testid="nuevo-turno-btn">
          <Plus size={16} />
          Nuevo turno
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <div key={i} className="stat-card animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-24 mb-3" />
                <div className="h-8 bg-slate-100 rounded w-16" />
              </div>
            ))
          : statCards.map((card) => (
              <div key={card.label} className="stat-card" data-testid={`stat-${card.label.replace(/ /g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{card.label}</p>
                  <div className={`w-8 h-8 ${card.color} rounded-lg flex items-center justify-center`}>
                    <card.icon size={16} className={card.iconColor} />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900">{card.value}</p>
              </div>
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Estado breakdown */}
        {stats && (
          <div className="bg-white border border-slate-100 rounded-2xl p-6">
            <h3 className="font-bold text-slate-900 mb-4">Por estado</h3>
            <div className="space-y-3">
              {[
                { label: "Pendientes", val: stats.pendientes, color: "bg-amber-400" },
                { label: "Confirmados", val: stats.confirmados, color: "bg-blue-500" },
                { label: "Completados", val: stats.completados, color: "bg-emerald-500" },
                { label: "Cancelados", val: stats.cancelados, color: "bg-red-400" },
              ].map(({ label, val, color }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${color} flex-shrink-0`} />
                  <span className="text-sm text-slate-600 flex-1">{label}</span>
                  <span className="text-sm font-bold text-slate-900">{val}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-50">
                <div className="flex items-center gap-3">
                  <DollarSign size={12} className="text-slate-400" />
                  <span className="text-sm text-slate-600 flex-1">Pagados</span>
                  <span className="text-sm font-bold text-slate-900">{stats.pagados}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hoy */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Hoy</h3>
            <span className="text-xs text-slate-400">{todayTurnos.length} turnos</span>
          </div>
          {todayTurnos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Clock size={28} className="text-slate-200 mb-2" />
              <p className="text-sm text-slate-400">Sin turnos para hoy</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {todayTurnos.slice(0, 5).map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 cursor-pointer"
                  onClick={() => t.dni && navigate(`/paciente/${t.dni}`)}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-700 font-bold text-xs">
                      {(t.nombre?.[0] || "?").toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {t.nombre} {t.apellido}
                    </p>
                    <p className="text-xs text-slate-400">{t.hora_turno} · {t.servicio || "Consulta"}</p>
                  </div>
                  <EstadoBadge estado={t.estado} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recientes */}
        <div className="bg-white border border-slate-100 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Últimos turnos</h3>
            <button onClick={() => navigate("/turnos")} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Ver todos <ChevronRight size={12} />
            </button>
          </div>
          {recentTurnos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <ClipboardCheck size={28} className="text-slate-200 mb-2" />
              <p className="text-sm text-slate-400">No hay turnos registrados</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentTurnos.slice(0, 6).map((t) => (
                <div
                  key={t.id}
                  className="turno-row flex items-center gap-3 p-2 cursor-pointer"
                  onClick={() => t.dni && navigate(`/paciente/${t.dni}`)}
                  data-testid="turno-row"
                >
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-600 font-bold text-xs">
                      {(t.nombre?.[0] || "?").toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {t.nombre} {t.apellido}
                    </p>
                    <p className="text-xs text-slate-400">{t.fecha_turno} {t.hora_turno}</p>
                  </div>
                  <EstadoBadge estado={t.estado} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NuevoTurnoModal isOpen={showModal} onClose={() => setShowModal(false)} onSuccess={load} />
    </div>
  );
}
